import { compact } from '@quromedical/utils';

import type { ErrorResult, SuccessResult } from './internal';

/**
 * a function that takes in a set of parameters and returns a success result
 */
export type SuccessResolver<TSuccess, TParams> = (
  params: TParams,
) => SuccessResult<TSuccess>;

export type SuccessResolvers<
  TRuleName extends string,
  TSuccess,
  TParams,
> = Partial<Record<TRuleName, SuccessResolver<TSuccess, TParams>>>;

/**
 * a function that takes in a set of parameters and returns an error result or undefined.
 */
export type AssertRule<TParams> = (params: TParams) => ErrorResult | undefined;

export type AssertRules<TRuleName extends string, TParams> = Partial<
  Record<TRuleName, AssertRule<TParams>[]>
>;

/**
 * a function that takes in a set of parameters and returns a success or error result
 */
export type InvokeRules<TSuccess, TParams> = (
  params: TParams,
) => SuccessResult<TSuccess> | ErrorResult;

/**
 * a function that takes in a set of parameters and returns a rule name
 */
export type ComposeParams<TSuccess, TParams, TRuleName extends string> = {
  /**
   * a function that takes in a set of parameters and returns a rule name.
   */
  getRuleName: (params: TParams) => TRuleName;
  /**
   * an optional record of rule names to an array of assertions. The assertions are run in
   * order and the first error result is returned. If no error result is returned, the rule is considered successful.
   */
  assertions?: Partial<Record<TRuleName, AssertRule<TParams>[]>>;
  /**
   * an array of assertions that are run by default.
   */
  defaultAssertions: AssertRule<TParams>[];
  /**
   * an optional record of rule names to a success resolver.
   */
  resolvers?: SuccessResolvers<TRuleName, TSuccess, TParams>;
  /**
   * a success resolver that is run by default.
   */
  defaultResolver: SuccessResolver<TSuccess, TParams>;
};

const mergeErrors = (errors: ErrorResult[]): ErrorResult => {
  const messages = errors.map((error) => error.messages).flat();

  return {
    type: 'error',
    messages,
  };
};

/**
 * this function takes in a set of parameters and returns a function that takes in a set of
 * parameters and returns a success or error result. The function is composed of a set of rules
 * that are run in order. The first error result is returned.
 * If no error result is returned, the rule is considered successful.
 * @returns a function that takes in a set of parameters and returns a success or error result
 */
export const compose = <TSuccess, TParams, TRuleName extends string>(
  /**
   * a set of parameters to compose a set of rules
   */
  composeParams: ComposeParams<TSuccess, TParams, TRuleName>,
): InvokeRules<TSuccess, TParams> => {
  const {
    assertions,
    defaultAssertions,
    defaultResolver,
    getRuleName,
    resolvers,
  } = composeParams;

  const invokeRules: InvokeRules<TSuccess, TParams> = (params) => {
    const ruleName = getRuleName(params);

    const ruleAssertion = assertions?.[ruleName];
    const resolvedAssertion: AssertRule<TParams>[] =
      ruleAssertion || defaultAssertions;
    const result = resolvedAssertion.map((assertion) => assertion(params));

    const errors = compact(result);
    const hasErrors = errors.length > 0;

    if (hasErrors) {
      return mergeErrors(errors);
    }

    const ruleResolver = resolvers?.[ruleName];
    const resolve: SuccessResolver<TSuccess, TParams> =
      ruleResolver || defaultResolver;

    return resolve(params);
  };

  return invokeRules;
};

/**
 * Create a resolver that will return a `successs` result with the given `value`
 */
export const resolveConstant =
  <T>(value: T): SuccessResolver<T, unknown> =>
  () => ({
    type: 'success',
    value,
  });

export type ComposeValidatorParams<TParams, TRuleName extends string> = Pick<
  ComposeParams<true, TParams, TRuleName>,
  'getRuleName' | 'assertions' | 'defaultAssertions'
>;

export type ValidateRules<TParams> = (
  params: TParams,
) => ErrorResult | undefined;

/**
 * Creates a validator that will resolve errors if the assertions are not met
 * Otherwise will return undefined
 */
export const composeValidator = <TParams, TRuleName extends string>(
  composeParams: ComposeValidatorParams<TParams, TRuleName>,
): ValidateRules<TParams> => {
  const resolve = compose({
    ...composeParams,
    defaultResolver: resolveConstant(true),
    resolvers: {},
  });

  return (params) => {
    const result = resolve(params);

    if (result.type === 'success') {
      return undefined;
    }

    return result;
  };
};

/**
 * Create a rule that will always return an error result with the provided message
 */
export const assertError = (message: string) => (): ErrorResult => ({
  type: 'error',
  messages: [message],
});
